import { FormEvent, useCallback } from 'react';

import { RecaptchaAction } from './RecaptchaAction';
import { DATADOG_USER_AGENT_REGEX } from './constants';
import { useRecaptchaCallback } from './useRecaptchaCallback';

export interface FormWithRecaptchaConfig {
    action: RecaptchaAction;
    onSubmit: (formData: FormData, recaptchaToken?: string) => Promise<void> | void;
    onError?: () => void;
}

/**
 * Hook that combines form submission with recaptcha validation.
 * Handles the recaptcha flow and calls the provided onSubmit callback with the token.
 */
export function useFormWithRecaptcha({
    action,
    onSubmit,
    onError,
}: FormWithRecaptchaConfig): (event: FormEvent<HTMLFormElement>) => void {
    const recaptchaCallback = useRecaptchaCallback(action);

    return useCallback(
        (event: FormEvent<HTMLFormElement>): void => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);

            if (DATADOG_USER_AGENT_REGEX.test(navigator.userAgent)) {
                void onSubmit(formData, '');
                return;
            }

            recaptchaCallback()
                .then((recaptchaToken) => {
                    return onSubmit(formData, recaptchaToken);
                })
                .catch(() => {
                    onError?.();
                });
        },
        [recaptchaCallback, onSubmit, onError],
    );
}
